import { format, parseISO, isValid } from 'date-fns';
import { es } from 'date-fns/locale';
import { DATE_FORMATS, USER_ROLES } from './constants';

// Date utilities
export const formatDate = (date, formatStr = DATE_FORMATS.DISPLAY) => {
  if (!date) return '';

  let dateObj = date;
  if (typeof date === 'string') {
    dateObj = parseISO(date);
  }

  if (!isValid(dateObj)) return '';

  return format(dateObj, formatStr, { locale: es });
};

export const formatDateForAPI = (date) => {
  return formatDate(date, DATE_FORMATS.API);
};

export const formatDateTime = (date) => {
  return formatDate(date, DATE_FORMATS.DATETIME);
};

export const getTodayFormatted = () => {
  return getTodayLocal();
};

// Get today's date in local timezone as YYYY-MM-DD format
export const getTodayLocal = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Get date relative to today in local timezone as YYYY-MM-DD format
export const getDateRelativeToToday = (daysOffset) => {
  const date = new Date();
  date.setDate(date.getDate() + daysOffset);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Get date relative to a given date in local timezone as YYYY-MM-DD format
export const getDateRelativeToDate = (dateString, daysOffset) => {
  const date = new Date(dateString);
  date.setDate(date.getDate() + daysOffset);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Role utilities
export const getRoleName = (role) => {
  const roleNames = {
    [USER_ROLES.PLATFORM_ADMIN]: 'Administrador Plataforma',
    [USER_ROLES.SALES]: 'Ventas',
    [USER_ROLES.ESTABLISHMENT_ADMIN]: 'Administrador Establecimiento',
    [USER_ROLES.MANAGER]: 'Encargado',
    [USER_ROLES.RECEPTIONIST]: 'Recepcionista',
    [USER_ROLES.CLEANING_STAFF]: 'Personal de Limpieza'
  };
  return roleNames[role] || role;
};

export const canManageUsers = (role) => {
  return [USER_ROLES.PLATFORM_ADMIN, USER_ROLES.SALES, USER_ROLES.ESTABLISHMENT_ADMIN, USER_ROLES.MANAGER].includes(role);
};

export const canManageRooms = (role) => {
  return [USER_ROLES.PLATFORM_ADMIN, USER_ROLES.ESTABLISHMENT_ADMIN, USER_ROLES.MANAGER].includes(role);
};

export const canManageCommonSpaces = (role) => {
  return [
    USER_ROLES.PLATFORM_ADMIN,
    USER_ROLES.ESTABLISHMENT_ADMIN,
    USER_ROLES.MANAGER,
    USER_ROLES.RECEPTIONIST
  ].includes(role);
};

export const canCreateOrders = (role) => {
  return [
    USER_ROLES.PLATFORM_ADMIN,
    USER_ROLES.ESTABLISHMENT_ADMIN,
    USER_ROLES.MANAGER,
    USER_ROLES.RECEPTIONIST
  ].includes(role);
};

export const canManageEstablishments = (role) => {
  return [USER_ROLES.PLATFORM_ADMIN, USER_ROLES.SALES].includes(role);
};

export const canViewEstablishmentDetails = (role) => {
  // Sales users can list and create establishments but cannot view details
  return role !== USER_ROLES.SALES && [USER_ROLES.PLATFORM_ADMIN, USER_ROLES.ESTABLISHMENT_ADMIN, USER_ROLES.MANAGER].includes(role);
};

export const canViewIncidents = (role) => {
  return [
    USER_ROLES.PLATFORM_ADMIN,
    USER_ROLES.ESTABLISHMENT_ADMIN,
    USER_ROLES.MANAGER,
    USER_ROLES.RECEPTIONIST,
    USER_ROLES.CLEANING_STAFF
  ].includes(role);
};

export const canManageSubscription = (role) => {
  return [USER_ROLES.PLATFORM_ADMIN, USER_ROLES.SALES, USER_ROLES.ESTABLISHMENT_ADMIN].includes(role);
};

export const canManageEstablishmentSettings = (role) => {
  return [USER_ROLES.PLATFORM_ADMIN, USER_ROLES.ESTABLISHMENT_ADMIN, USER_ROLES.MANAGER].includes(role);
};

export const canViewReports = (role) => {
  return [USER_ROLES.PLATFORM_ADMIN, USER_ROLES.ESTABLISHMENT_ADMIN, USER_ROLES.MANAGER].includes(role);
};

export const canManageTimeEntries = (role) => {
  return [USER_ROLES.PLATFORM_ADMIN, USER_ROLES.ESTABLISHMENT_ADMIN, USER_ROLES.MANAGER].includes(role);
};

// Get default route for a user role
export const getDefaultRouteForRole = (role) => {
  if (role === USER_ROLES.CLEANING_STAFF) {
    return '/orders/mobile';
  }
  if (role === USER_ROLES.SALES) {
    return '/admin/establishments';
  }
  return '/dashboard';
};

// Form utilities
export const getFormErrorMessage = (errors, fieldName) => {
  const error = errors[fieldName];
  if (!error) return '';

  if (error.type === 'required') {
    return 'Este campo es requerido';
  }

  if (error.type === 'pattern') {
    return 'Formato inválido';
  }

  if (error.type === 'minLength') {
    return `Mínimo ${error.message || 3} caracteres`;
  }

  return error.message || 'Campo inválido';
};

// Device detection
export const isMobile = () => {
  return window.innerWidth <= 768;
};

export const isTablet = () => {
  return window.innerWidth > 768 && window.innerWidth <= 1024;
};

export const isDesktop = () => {
  return window.innerWidth > 1024;
};

// Keyboard navigation utilities
export const handleTabNavigation = (e, currentRow, currentCol, maxRows, maxCols, onCellChange) => {
  if (e.key === 'Tab') {
    e.preventDefault();
    let newCol = currentCol + 1;
    let newRow = currentRow;

    if (newCol >= maxCols) {
      newCol = 0;
      newRow = currentRow + 1;
    }

    if (newRow >= maxRows) {
      newRow = 0;
    }

    onCellChange(newRow, newCol);
  }
};

export const handleEnterNavigation = (e, currentRow, currentCol, maxRows, onCellChange) => {
  if (e.key === 'Enter') {
    e.preventDefault();
    const newRow = currentRow + 1;

    if (newRow >= maxRows) {
      onCellChange(0, currentCol);
    } else {
      onCellChange(newRow, currentCol);
    }
  }
};

export const handleArrowNavigation = (e, currentRow, currentCol, maxRows, maxCols, onCellChange) => {
  switch (e.key) {
    case 'ArrowUp':
      e.preventDefault();
      onCellChange(currentRow > 0 ? currentRow - 1 : maxRows - 1, currentCol);
      break;
    case 'ArrowDown':
      e.preventDefault();
      onCellChange(currentRow < maxRows - 1 ? currentRow + 1 : 0, currentCol);
      break;
    case 'ArrowLeft':
      e.preventDefault();
      onCellChange(currentRow, currentCol > 0 ? currentCol - 1 : maxCols - 1);
      break;
    case 'ArrowRight':
      e.preventDefault();
      onCellChange(currentRow, currentCol < maxCols - 1 ? currentCol + 1 : 0);
      break;
  }
};

// Local storage utilities
export const setStorageItem = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error('Error saving to localStorage:', error);
  }
};

export const getStorageItem = (key) => {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : null;
  } catch (error) {
    console.error('Error reading from localStorage:', error);
    return null;
  }
};

export const removeStorageItem = (key) => {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.error('Error removing from localStorage:', error);
  }
};

// Status utilities
export const getStatusColor = (status) => {
  const colors = {
    pending: 'text-yellow-600 bg-yellow-50',
    in_progress: 'text-blue-600 bg-blue-50',
    completed: 'text-green-600 bg-green-50',
    cancelled: 'text-red-600 bg-red-50',
    dirty: 'text-red-600 bg-red-50',
    cleaning: 'text-yellow-600 bg-yellow-50',
    clean: 'text-green-600 bg-green-50',
    out_of_order: 'text-gray-600 bg-gray-50'
  };
  return colors[status] || 'text-gray-600 bg-gray-50';
};

export const getStatusLabel = (status) => {
  const labels = {
    pending: 'Pendiente',
    in_progress: 'En Progreso',
    completed: 'Completado',
    cancelled: 'Cancelado',
    dirty: 'Sucia',
    cleaning: 'Limpiando',
    clean: 'Limpia',
    out_of_order: 'Fuera de Servicio',
    checkout: 'Check-out',
    maintenance: 'Mantenimiento',
    deep_clean: 'Limpieza Profunda',
    inspection: 'Inspección'
  };
  return labels[status] || status;
};